/**
 * Where every browser storage key of the app (web, desktop, iOS) stands at sign-out, in one
 * place. A key that holds a person's or a tenant's data is deleted by `performLogout`: by name or
 * by prefix, here. A key that must outlive the person names why, here too. The gate
 * (`signOutStorage.test.ts`) fails on a storage key declared anywhere else and listed nowhere, so
 * a new store cannot slip past sign-out unnoticed.
 *
 * A new per-person store belongs under `personalKey` (`utils/personalStorage.ts`): its prefix is
 * already deleted wholesale. Display choices keyed per device go under `DEVICE_WIDE`.
 *
 * Only constants here: `logout.ts` and the gate both read it, and it must import nothing heavy.
 */
import { ADMIN_CHAT_STORAGE_PREFIX } from "./adminChatStorage";
import { ADMISSION_HANDOFF_KEY } from "./admissionHandoff";
import { AUTH_REDIRECT_KEY } from "./authReturn";
import { LAST_PROJECT_KEY } from "./lastProject";
import { PERSONAL_PREFIX } from "./personalStorage";
import { FEATURE_FLAG_STORAGE_KEY_PREFIX } from "../hooks/featureFlagStorageKey";

/** localStorage keys sign-out deletes. */
export const SIGN_OUT_LOCAL_KEYS: readonly string[] = [
    "embed_organization_cache",
    "embed_member_cache",
    AUTH_REDIRECT_KEY,
    // The project a bare link opens: this account's last project, never the next one's.
    LAST_PROJECT_KEY,
    // Marvin's drawer: its look, and, from earlier builds, a thread with nobody's name on it.
    "marvin-sidekick-thread",
    // From earlier builds, never read again: conversation anchors with nobody's name on them.
    "marvin-conversation-anchors-v1",
    // The file cache's index of its folders and their access policies; the folders go with
    // `clearFileCache`.
    "pp_opfs_scope_namespaces",
    "pp_opfs_access_policy",
    // Desktop: the window's saved workspace and tab strip, its own project, the projects with
    // a tab popped out, the project a new page is opening for, and project Home photos.
    "primepoint.workspace.v1",
    "primepoint.window-tabs.v1",
    "primepoint.own-project.v1",
    "primepoint.popped-out-projects.v1",
    "primepoint.page-opening.v1",
    "primepoint.project-photos.v1",
];

/** localStorage key prefixes sign-out deletes. "primepoint:" covers org-scoped caches (recent
 *  searches, logo and background URLs); the flag snapshot also carries the tenant's id. */
export const SIGN_OUT_LOCAL_PREFIXES: readonly string[] = [
    "prime_settings_",
    "prime_user_settings_",
    "primepoint:",
    "conversationEngine:",
    FEATURE_FLAG_STORAGE_KEY_PREFIX,
    // Admin-chat thread pointers: this account's conversations, never the next one's.
    ADMIN_CHAT_STORAGE_PREFIX,
    // Each person's work in progress (`utils/personalStorage.ts`).
    PERSONAL_PREFIX,
    // The projects this person picked lately, kept per email.
    "pp.recentProjects:",
    // Markup the prototype kept by file only, before markup moved to the server: nobody's, so
    // it is discarded, never read or migrated.
    "markup.v2.",
    // Desktop: document tab sessions (titles, routes) and, from browser mode, recent files.
    "primepoint-desktop:",
];

/** This tab's sessionStorage keys sign-out deletes: the post-login destination, a project
 *  switch's credential on its way to the next page, the desktop hand-off, the Miranda notices
 *  acted on, the desktop switch cover, and the project searches already counted. */
export const SIGN_OUT_SESSION_KEYS: readonly string[] = [
    AUTH_REDIRECT_KEY,
    ADMISSION_HANDOFF_KEY,
    "primepoint.desktop-handoff.v1",
    "pp.mirandaRunNotices.v1",
    "primepoint.project-switch.v1",
    "primepoint.searchRunCounted",
];

/** sessionStorage key prefixes sign-out deletes. */
export const SIGN_OUT_SESSION_PREFIXES: readonly string[] = [
    // Each person's work in progress (`utils/personalStorage.ts`), and a citation's rooms, set
    // and cranes kept for "Open in Models" (`shared/modelLinkExtras.ts`).
    PERSONAL_PREFIX,
    // From earlier builds, never read again: the same work in progress with nobody's name on it.
    "submittal-package-note:",
    "submittal-marvin-run:",
    "submittal-placements:",
    "detector.pendingAccepts",
];

/** Keys (or prefixes) that outlive sign-out on purpose: none holds a tenant's content. */
export const DEVICE_WIDE: Readonly<Record<string, string>> = {
    "pp-viewer:appearance": "this device's text size and density",
    "pp-viewer:mode": "this device's light or dark theme",
    serverOverride: "which server this device talks to",
    apiEnvironmentOverride: "which server this device talks to (older builds)",
    "documentCompare.redlineChangesOnly": "a display choice",
    "page-aside-folded": "a display choice",
    "pp-lists:paging": "a display choice (older builds)",
    "pp-viewer:view-filter": "a display choice",
    "primepoint.workflowFields.preview": "a display choice",
    tabletShell: "a display choice",
    drawings3dCamera: "a display choice",
    "pp-model-minimap":
        "where this device keeps the model's sheet minimap window, a display choice",
    "pp.model-pane-window": "where this tab keeps the model page's floating pane, a display choice",
    "pp-viewer-navigation": "the model viewer's mouse (Settings → Navigation), a display choice",
    "pp-marvin-sheet:layout:": "a display choice",
    shopDrawingDismissedBars: "a display choice",
    "pp-style-lab:choice": "a developer's style choice",
    "pp.viewer.firstHop": "a display choice",
    "pp-workbench:stage-theme": "a display choice",
    "marvin-explore-callouts": "a display choice",
    "pp-explore:try-row-dismissed": "a dismissed suggestion, kept per browser",
    "pp-whats-new-seen:": "which release notes this device has shown, per organization",
    "pp.signedOut": "the sign-out broadcast itself, which must outlive sign-out",
    "pp.navHistory.v1": "what each entry of this tab's history was; the browser keeps the entries",
    "primepoint-query-cache-epoch":
        "outlives sign-out so query snapshots from an earlier sign-in never restore",
    "pp.signIn.last": "the sign-in screen offers the last account again; holds no tenant data",
    "pp.signIn.invite": "an invitation link waiting for sign-in",
    primepoint_auth_provider: "the sign-in method this device used last",
    "primepoint_auth_flow:": "a sign-in in progress",
    stytch_sdk_state_: "the sign-in SDK's own state, ended by revoking the session",
    "pp.catalogFallback": "a two-minute marker that admission fell back to the catalog",
    "pp.companyAuthTried:": "this tab tried company sign-in for an address",
    "pp.refusalReloaded:": "this tab reloaded once for a refusal at an address",
    "pp.reportedAdmissionNotice:": "this tab already showed an admission notice at an address",
};

/** Keys another module owns and deletes at sign-out itself. */
export const CLEARED_BY_OWNER: Readonly<Record<string, string>> = {
    "primepointNativeOffline:": "the prefs store's fallback; `prefs.clear()` at sign-out",
    "primepoint-cache-": "query cache databases; `clearAllPersistedCaches` at sign-out",
    "primepoint-cache-databases": "the query cache's registry; `clearAllPersistedCaches`",
    "primepoint-query-cache-scopes": "the query cache's registry; `clearAllPersistedCaches`",
    REACT_QUERY_OFFLINE_CACHE_: "query snapshots in IndexedDB; `clearAllPersistedCaches`",
    primepoint_embed_session_jwt: "the embed sign-in; `setTokens(null)` at sign-out",
    primepoint_embed_session_token: "the embed sign-in; `setTokens(null)` at sign-out",
    primepoint_embed_session_exp: "the embed sign-in; `setTokens(null)` at sign-out",
};

/** Whether sign-out deletes `key` from localStorage. */
export function signOutClearsLocal(key: string): boolean {
    return (
        SIGN_OUT_LOCAL_KEYS.includes(key) ||
        SIGN_OUT_LOCAL_PREFIXES.some((prefix) => key.startsWith(prefix))
    );
}

/** Whether sign-out deletes `key` from sessionStorage. */
export function signOutClearsSession(key: string): boolean {
    return (
        SIGN_OUT_SESSION_KEYS.includes(key) ||
        SIGN_OUT_SESSION_PREFIXES.some((prefix) => key.startsWith(prefix))
    );
}
